/* eslint-disable react/jsx-no-useless-fragment */
import {
  CloseCircleOutlined,
  FileImageOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { AccelFile, FileType } from "@axl-dev/school-sdk";
import { Tooltip, Space, Progress } from "antd";
import cn from "classnames";
import { useCallback, useMemo, useState } from "react";

import { useFileProvider, useLoc } from "@app/shared/lib/hooks";

import FileDropzone from "../FileDropzone/FileDropzone";
import { FileUploader } from "../FileUploader";

import styles from "./ImagePicker.module.scss";

type ImagePickerProps = {
  src?: string;
  srcFile?: AccelFile | null;
  readonly?: boolean;
  placeholder?: React.ReactNode;
  className?: string;
  align?: "left" | "center" | "right";
  style?: React.CSSProperties;
  imageStyles?: React.CSSProperties;
  onChange?: (file?: AccelFile) => void;
  onUploadingChange?: (uploading: boolean) => void;
};

export const ImagePicker: React.FC<ImagePickerProps> = ({
  src,
  srcFile,
  placeholder: _placeholder,
  className,
  readonly = false,
  style,
  imageStyles,
  align = "center",
  onChange,
  onUploadingChange,
}) => {
  const loc = useLoc();
  const fileProvider = useFileProvider();
  const dropzoneHandle = FileDropzone.useHandler();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileToUpload, setFileToUpload] = useState<AccelFile>();

  const imageSrc = useMemo(() => {
    if (fileToUpload) return fileToUpload.previewImage;

    return srcFile ? fileProvider.getUrl(srcFile) : src;
  }, [fileToUpload, srcFile, fileProvider, src]);

  const placeholder = useMemo(() => {
    return (
      _placeholder ?? (
        <Space
          direction="vertical"
          size={2}
          align="center"
          className="color-secondary w-100 p-5 justify-center h-100"
        >
          <FileImageOutlined className="fs-18" />
          <div className="text-center">
            {loc.word("ImagePicker.placeholderMsg", {
              default: "Click to select image",
            })}
          </div>
        </Space>
      )
    );
  }, [_placeholder, loc]);

  const selectFile = useCallback(() => {
    if (readonly === true) return;
    dropzoneHandle.open();
  }, [dropzoneHandle, readonly]);

  const change = useCallback(
    (file?: AccelFile) => {
      if (readonly === true) return;
      onChange?.(file);
    },
    [readonly, onChange],
  );

  return (
    <>
      <div
        className={cn(styles.img_picker, className, "relative")}
        data-has-image={imageSrc != null}
        style={style}
      >
        <div
          className="w-100 flex"
          style={{ justifyContent: align }}
          onClick={selectFile}
        >
          {imageSrc ? (
            <img alt="" src={imageSrc} style={imageStyles} />
          ) : (
            placeholder
          )}
        </div>
        {readonly !== true && (
          <div className={styles.img_picker_overlay} data-visible={uploading}>
            {!uploading ? (
              <>
                <Tooltip
                  title={loc.word("ImagePicker.selectMsg", {
                    default: "Select new image",
                  })}
                >
                  <PlusCircleOutlined
                    readOnly={readonly}
                    className="mr-10"
                    onClick={() => selectFile()}
                  />
                </Tooltip>
                <Tooltip title={loc.word("Delete")}>
                  <CloseCircleOutlined
                    readOnly={readonly}
                    onClick={() => {
                      change(undefined);
                      setFileToUpload(undefined);
                    }}
                  />
                </Tooltip>
              </>
            ) : (
              <Progress type="circle" percent={uploadProgress} width={40} />
            )}
          </div>
        )}
      </div>

      <FileDropzone
        handle={dropzoneHandle}
        noClick
        onUpload={(x) => {
          if (x.length === 0) return;
          setFileToUpload(x[0]);
          setUploading(true);
        }}
        onCancel={() => setFileToUpload(undefined)}
        acceptTypes={[FileType.Image]}
        multiple={false}
        className="d-none"
      >
        <></>
      </FileDropzone>

      {fileToUpload && (
        <FileUploader
          key={fileToUpload.key}
          file={fileToUpload}
          onReady={() => {
            setUploading(true);
            onUploadingChange?.(true);
          }}
          onProgress={(x) => setUploadProgress(x.percent)}
          onError={() => {
            onUploadingChange?.(false);
            setFileToUpload(undefined);
            setUploading(false);
          }}
          onComplete={async ({ file }) => {
            onUploadingChange?.(false);
            change(file);
            setUploadProgress(0);
            setUploading(false);
          }}
        />
      )}
    </>
  );
};
